import { useState, useEffect, useRef } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
    Loader2, Upload, CheckCircle2, XCircle, Clock,
    Smartphone, CreditCard, Shield, ArrowRight, Copy,
} from "lucide-react";

function initials(name = "") {
    return name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
}

function toB64(file) {
    return new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(",")[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
    });
}

function DocUpload({ label, hint, file, onChange }) {
    const ref = useRef();
    return (
        <div>
            <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">{label}</div>
            <div
                onClick={() => ref.current.click()}
                className="border-2 border-dashed border-slate-200 hover:border-[#10B1B6] rounded-2xl p-5 text-center cursor-pointer transition-colors group"
            >
                {file ? (
                    <div className="flex items-center justify-center gap-2 text-emerald-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-medium">{file.name}</span>
                    </div>
                ) : (
                    <>
                        <Upload className="w-6 h-6 text-slate-400 group-hover:text-[#10B1B6] mx-auto mb-1 transition-colors" />
                        <div className="text-sm text-slate-500 group-hover:text-[#10B1B6] transition-colors">Click to upload {label}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{hint}</div>
                    </>
                )}
            </div>
            <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={onChange} />
        </div>
    );
}

export default function KYCPage() {
    const { user } = useAuth();
    const [status,           setStatus]           = useState(null);
    const [loading,          setLoading]          = useState(true);
    const [selectedMethod,   setSelectedMethod]   = useState(null);
    const [verifying,        setVerifying]        = useState(false);
    const [submitting,       setSubmitting]       = useState(false);
    const [docs,             setDocs]             = useState({ aadhaar_front: null, aadhaar_back: null, pan: null, selfie: null });
    const [copied,           setCopied]           = useState(false);

    // Aadhaar OTP
    const [aadhaarNumber, setAadhaarNumber] = useState("");
    const [otp,           setOtp]           = useState("");
    const [otpSent,       setOtpSent]       = useState(false);
    const [otpLoading,    setOtpLoading]    = useState(false);

    // PAN
    const [panNumber, setPanNumber] = useState("");
    const [panLoading, setPanLoading] = useState(false);

    // Digilocker
    const [digilockerLoading, setDigilockerLoading] = useState(false);

    // Right sidebar data
    const [preview, setPreview] = useState(null);
    const [members, setMembers] = useState(null);

    useEffect(() => {
        fetchStatus();
        api.get("/pairing/preview").then(r => setPreview(r.data)).catch(() => {});
        api.get("/pairing/members-status").then(r => setMembers(r.data)).catch(() => {});

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        if (code && state && window.location.pathname === "/kyc/digilocker/callback") {
            handleDigilockerCallback(code, state);
        }
    }, []);

    const handleDigilockerCallback = async (code, state) => {
        try {
            setDigilockerLoading(true);
            await api.post("/webhooks/digilocker/callback", {
                code, state, user_id: state,
                redirect_uri: window.location.origin + "/kyc/digilocker/callback",
            });
            toast.success("Digilocker verification successful!");
            window.history.replaceState({}, document.title, "/app/kyc");
            fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.message || "Digilocker verification failed");
        } finally {
            setDigilockerLoading(false);
        }
    };

    const fetchStatus = async () => {
        try { const r = await api.get("/kyc/status"); setStatus(r.data); } catch (_) {}
        setLoading(false);
    };

    const setDoc = key => e => {
        const file = e.target.files[0];
        if (file) setDocs(d => ({ ...d, [key]: file }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!docs.aadhaar_front && !docs.aadhaar_back && !docs.pan && !docs.selfie) {
            toast.error("Please upload at least one document"); return;
        }
        setSubmitting(true);
        try {
            const payload = {};
            if (docs.aadhaar_front) payload.aadhaar_front_b64 = await toB64(docs.aadhaar_front);
            if (docs.aadhaar_back)  payload.aadhaar_back_b64  = await toB64(docs.aadhaar_back);
            if (docs.pan)           payload.pan_b64           = await toB64(docs.pan);
            if (docs.selfie)        payload.selfie_b64        = await toB64(docs.selfie);
            await api.post("/kyc/submit", payload);
            toast.success("Documents submitted! Our team will review within 24–48 hours.");
            setDocs({ aadhaar_front: null, aadhaar_back: null, pan: null, selfie: null });
            fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSendOtp = async () => {
        if (!aadhaarNumber || aadhaarNumber.length !== 12) { toast.error("Please enter a valid 12-digit Aadhaar number"); return; }
        setOtpLoading(true);
        try {
            await api.post("/kyc/aadhaar-otp/initiate", { aadhaar_number: aadhaarNumber });
            setOtpSent(true);
            toast.success("OTP sent to your Aadhaar-linked mobile number");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send OTP");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp || otp.length !== 6) { toast.error("Please enter a valid 6-digit OTP"); return; }
        setVerifying(true);
        try {
            await api.post("/kyc/aadhaar-otp/verify", { otp });
            toast.success("Aadhaar verified successfully!");
            setSelectedMethod(null); setOtpSent(false); setOtp(""); setAadhaarNumber("");
            fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || "OTP verification failed");
        } finally {
            setVerifying(false);
        }
    };

    const handleVerifyPan = async () => {
        if (!panNumber || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)) {
            toast.error("Please enter a valid PAN number (format: AAAAA9999A)"); return;
        }
        setPanLoading(true);
        try {
            await api.post("/kyc/pan/verify", { pan_number: panNumber });
            toast.success("PAN verified successfully!");
            setSelectedMethod(null); setPanNumber("");
            fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || "PAN verification failed");
        } finally {
            setPanLoading(false);
        }
    };

    const handleDigilocker = async () => {
        setDigilockerLoading(true);
        try {
            const r = await api.post("/kyc/digilocker/initiate", {
                redirect_uri: `${window.location.origin}/kyc/digilocker/callback`,
            });
            window.location.href = r.data.auth_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to initiate Digilocker verification");
            setDigilockerLoading(false);
        }
    };

    const copyInvite = () => {
        navigator.clipboard.writeText(`${window.location.origin}/register?ref=${user?.referral_code}`).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const resetMethod = () => {
        setSelectedMethod(null); setOtpSent(false); setOtp(""); setAadhaarNumber("");
    };

    /* ── sidebar ── */
    const leftCount    = preview?.left_count  ?? 0;
    const rightCount   = preview?.right_count ?? 0;
    const maxWing      = Math.max(leftCount, rightCount, 1);
    const minWing      = Math.min(leftCount, rightCount);
    const symPct       = Math.round((minWing / maxWing) * 100);
    const symSide      = leftCount >= rightCount ? "Left" : "Right";
    const leftLeaders  = members?.left_members?.slice(0, 3)  ?? [];
    const rightLeaders = members?.right_members?.slice(0, 3) ?? [];

    /* ── derived state ── */
    const isApproved = status?.kyc_status === "approved";
    const isPending  = status?.kyc_status === "pending";
    const isRejected = status?.kyc_status === "rejected";

    const verifiedDate = status?.kyc_verified_at
        ? new Date(status.kyc_verified_at).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "—";

    const method = status?.kyc_method || "";
    const channels = [
        {
            abbr: "AD",
            avatarCls: "bg-[#7059FD]/15 text-[#7059FD]",
            name: "Aadhaar Card Linkage",
            desc: "Connected with Verified OTP registry match",
            type: "UIDAI Node Match",
            verified: method.includes("aadhaar") || isApproved,
            label: "Connected",
        },
        {
            abbr: "PN",
            avatarCls: "bg-[#E45B80]/15 text-[#E45B80]",
            name: "Permanent Account Number (PAN)",
            desc: "NSDL Tax verification records linked",
            type: "Tax Registry Match",
            verified: method.includes("pan") || isApproved,
            label: "Verified",
        },
        {
            abbr: "BK",
            avatarCls: "bg-[#F59E0B]/15 text-[#F59E0B]",
            name: "Active Bank Account Linkage",
            desc: "IMPS Penny-drop test matched & validated",
            type: "IFS Commission route",
            verified: isApproved,
            label: "Validated",
        },
    ];
    const connectedCount = channels.filter(c => c.verified).length;

    return (
        <AppShell>
            <div className="flex gap-5 items-start -m-4 md:-m-8 h-full overflow-hidden">

                {/* ── CENTER (scrollable) ── */}
                <div className="flex-1 min-w-0 space-y-5 overflow-y-auto h-full p-4 md:p-8 pb-12">

                    {/* Header */}
                    <div className="flex justify-between items-center gap-4">
                        <div>
                            <div className="overline">Compliance</div>
                            <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5">
                                KYC Verification
                            </h1>
                        </div>
                        {user?.referral_code && (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 whitespace-nowrap border border-teal-200">
                                Sponsor ID: {user.referral_code}
                            </span>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-6 h-6 animate-spin text-[#10B1B6]" />
                        </div>

                    ) : isApproved ? (
                        <>
                            {/* ── Approved: 2-card top row ── */}
                            <div className="grid md:grid-cols-2 gap-5">

                                {/* Teal KYC status card */}
                                <div className="relative overflow-hidden rounded-3xl bg-[#10B1B6] p-6 flex flex-col justify-between min-h-[160px] shadow-sm">
                                    <div>
                                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/70">
                                            Regulatory Compliance
                                        </span>
                                        <div className="flex items-center gap-3 mt-2">
                                            <h2 className="text-2xl font-extrabold text-white leading-tight">
                                                KYC Status: Verified
                                            </h2>
                                            <div className="w-8 h-8 rounded-full bg-white/20 grid place-items-center shrink-0">
                                                <CheckCircle2 className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5">
                                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/60">
                                            Verification Stamp
                                        </span>
                                        <div className="text-white font-bold mt-0.5">Verified on {verifiedDate}</div>
                                    </div>
                                    <div className="absolute -right-6 -bottom-6 w-28 h-28 bg-white/10 rounded-full pointer-events-none" />
                                    <div className="absolute right-2 -bottom-10 w-16 h-16 bg-white/[0.08] rounded-full pointer-events-none" />
                                </div>

                                {/* Identity confirmed card */}
                                <div className="bg-white rounded-3xl shadow-sm p-6 flex flex-col justify-between">
                                    <div>
                                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#10B1B6]">
                                            Limits Unlocked
                                        </span>
                                        <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-1 leading-tight">
                                            Identity verified successfully
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                                            Your identity has been verified. Pairing bonuses, team matching credits,
                                            and withdrawals are fully unlocked for{" "}
                                            <span className="text-[#10B1B6] font-bold">
                                                partner ID {user?.referral_code ?? "—"}
                                            </span>.
                                        </p>
                                    </div>
                                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                            Instant Settlements Active
                                        </span>
                                        <button
                                            onClick={() => setSelectedMethod("upload")}
                                            className="text-xs font-semibold text-[#2172E5] hover:text-blue-700 transition-colors"
                                        >
                                            Simulate re-verify
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* ── Verification Channels ── */}
                            {!selectedMethod ? (
                                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
                                    <div className="px-6 py-5 flex items-center justify-between border-b border-slate-100">
                                        <div>
                                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                                                Identity Registry
                                            </span>
                                            <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-0.5">
                                                Verification Channels
                                            </h3>
                                        </div>
                                        <span className="px-3 py-1.5 bg-[#10B1B6]/10 text-[#10B1B6] text-xs font-bold rounded-full border border-[#10B1B6]/20">
                                            {connectedCount} channel{connectedCount !== 1 ? "s" : ""} connected
                                        </span>
                                    </div>

                                    <div className="divide-y divide-slate-50">
                                        {channels.map(ch => (
                                            <div key={ch.abbr} className="flex items-center gap-4 px-6 py-5 hover:bg-slate-50/50 transition-colors">
                                                <div className={`w-11 h-11 rounded-2xl grid place-items-center text-sm font-black shrink-0 ${ch.avatarCls}`}>
                                                    {ch.abbr}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold text-slate-800 text-sm">{ch.name}</div>
                                                    <div className="text-xs text-slate-400 mt-0.5">{ch.desc}</div>
                                                </div>
                                                <div className="text-xs text-slate-400 font-semibold hidden sm:block shrink-0">
                                                    {ch.type}
                                                </div>
                                                <div className="shrink-0">
                                                    {ch.verified ? (
                                                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full whitespace-nowrap">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                                            {ch.label}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full whitespace-nowrap">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" />
                                                            Pending
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                /* Re-verify form (upload) */
                                <VerificationForm
                                    selectedMethod={selectedMethod}
                                    onBack={resetMethod}
                                    otpSent={otpSent} setOtpSent={setOtpSent}
                                    aadhaarNumber={aadhaarNumber} setAadhaarNumber={setAadhaarNumber}
                                    otp={otp} setOtp={setOtp}
                                    otpLoading={otpLoading} verifying={verifying}
                                    onSendOtp={handleSendOtp} onVerifyOtp={handleVerifyOtp}
                                    panNumber={panNumber} setPanNumber={setPanNumber}
                                    panLoading={panLoading} onVerifyPan={handleVerifyPan}
                                    digilockerLoading={digilockerLoading} onDigilocker={handleDigilocker}
                                    docs={docs} setDoc={setDoc} submitting={submitting} onSubmit={onSubmit}
                                />
                            )}
                        </>

                    ) : (
                        <>
                            {/* ── Pending banner ── */}
                            {isPending && (
                                <div className="rounded-3xl overflow-hidden flex shadow-sm">
                                    <div className="w-1.5 shrink-0 bg-[#2172E5]" />
                                    <div className="flex-1 bg-[#0C2B5C] px-6 py-5 flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-2xl bg-[#2172E5]/20 flex items-center justify-center shrink-0">
                                            <Clock className="w-5 h-5 text-[#2172E5]" />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#2172E5]">Under Review</span>
                                            <h3 className="text-white font-extrabold text-lg leading-tight">KYC Documents Submitted</h3>
                                            <p className="text-white/50 text-xs mt-0.5">
                                                Our team is reviewing your documents. This typically takes 24–48 hours.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Rejected banner ── */}
                            {isRejected && (
                                <div className="rounded-3xl overflow-hidden flex shadow-sm">
                                    <div className="w-1.5 shrink-0 bg-[#E45B80]" />
                                    <div className="flex-1 bg-[#0C2B5C] px-6 py-5 flex items-center gap-4">
                                        <div className="w-11 h-11 rounded-2xl bg-[#E45B80]/20 flex items-center justify-center shrink-0">
                                            <XCircle className="w-5 h-5 text-[#E45B80]" />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#E45B80]">Verification Failed</span>
                                            <h3 className="text-white font-extrabold text-lg leading-tight">KYC Rejected</h3>
                                            {status?.kyc_rejection_reason && (
                                                <p className="text-white/60 text-xs mt-0.5">Reason: {status.kyc_rejection_reason}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Verification methods / forms (not pending) ── */}
                            {!isPending && (
                                !selectedMethod ? (
                                    <>
                                        <div className="bg-white rounded-3xl shadow-sm px-6 py-5">
                                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                                                Identity Verification
                                            </span>
                                            <h3 className="text-2xl font-extrabold text-[#0C2B5C] mt-0.5">
                                                Choose Verification Method
                                            </h3>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-5">
                                            {/* Aadhaar OTP */}
                                            <div className="bg-white rounded-3xl shadow-sm p-6 flex flex-col gap-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-11 h-11 rounded-2xl bg-[#7059FD]/15 flex items-center justify-center shrink-0">
                                                        <Smartphone className="w-5 h-5 text-[#7059FD]" />
                                                    </div>
                                                    <div>
                                                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Recommended</span>
                                                        <div className="font-extrabold text-[#0C2B5C]">Aadhaar OTP</div>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-500 leading-relaxed">
                                                    Verify instantly using OTP sent to your Aadhaar-linked mobile. Most secure method.
                                                </p>
                                                <button
                                                    onClick={() => setSelectedMethod("aadhaar_otp")}
                                                    className="mt-auto w-full py-2.5 bg-[#7059FD] hover:bg-purple-700 text-white text-sm font-bold rounded-2xl transition-colors flex items-center justify-center gap-2"
                                                >
                                                    Verify with Aadhaar OTP <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Digilocker */}
                                            <div className="bg-white rounded-3xl shadow-sm p-6 flex flex-col gap-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-11 h-11 rounded-2xl bg-[#2172E5]/15 flex items-center justify-center shrink-0">
                                                        <Shield className="w-5 h-5 text-[#2172E5]" />
                                                    </div>
                                                    <div>
                                                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Gov. Backed</span>
                                                        <div className="font-extrabold text-[#0C2B5C]">Digilocker</div>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-500 leading-relaxed">
                                                    Use your Digilocker account to verify Aadhaar or PAN instantly.
                                                </p>
                                                <button
                                                    onClick={handleDigilocker}
                                                    disabled={digilockerLoading}
                                                    className="mt-auto w-full py-2.5 bg-[#2172E5] hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-bold rounded-2xl transition-colors flex items-center justify-center gap-2"
                                                >
                                                    {digilockerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                                                    Verify with Digilocker
                                                </button>
                                            </div>

                                            {/* PAN */}
                                            <div className="bg-white rounded-3xl shadow-sm p-6 flex flex-col gap-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-11 h-11 rounded-2xl bg-[#E45B80]/15 flex items-center justify-center shrink-0">
                                                        <CreditCard className="w-5 h-5 text-[#E45B80]" />
                                                    </div>
                                                    <div>
                                                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Tax Registry</span>
                                                        <div className="font-extrabold text-[#0C2B5C]">PAN Card</div>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-500 leading-relaxed">
                                                    Verify using your PAN card number. Simple and quick verification.
                                                </p>
                                                <button
                                                    onClick={() => setSelectedMethod("pan")}
                                                    className="mt-auto w-full py-2.5 bg-[#E45B80] hover:bg-pink-700 text-white text-sm font-bold rounded-2xl transition-colors flex items-center justify-center gap-2"
                                                >
                                                    Verify with PAN <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Document Upload */}
                                            <div className="bg-white rounded-3xl shadow-sm p-6 flex flex-col gap-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-11 h-11 rounded-2xl bg-[#F59E0B]/15 flex items-center justify-center shrink-0">
                                                        <Upload className="w-5 h-5 text-[#F59E0B]" />
                                                    </div>
                                                    <div>
                                                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Manual Review</span>
                                                        <div className="font-extrabold text-[#0C2B5C]">Document Upload</div>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-500 leading-relaxed">
                                                    Upload Aadhaar, PAN, and selfie for manual review. Takes 24–48 hours.
                                                </p>
                                                <button
                                                    onClick={() => setSelectedMethod("upload")}
                                                    className="mt-auto w-full py-2.5 bg-[#F59E0B] hover:bg-amber-600 text-white text-sm font-bold rounded-2xl transition-colors flex items-center justify-center gap-2"
                                                >
                                                    Upload Documents <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <VerificationForm
                                        selectedMethod={selectedMethod}
                                        onBack={resetMethod}
                                        otpSent={otpSent} setOtpSent={setOtpSent}
                                        aadhaarNumber={aadhaarNumber} setAadhaarNumber={setAadhaarNumber}
                                        otp={otp} setOtp={setOtp}
                                        otpLoading={otpLoading} verifying={verifying}
                                        onSendOtp={handleSendOtp} onVerifyOtp={handleVerifyOtp}
                                        panNumber={panNumber} setPanNumber={setPanNumber}
                                        panLoading={panLoading} onVerifyPan={handleVerifyPan}
                                        digilockerLoading={digilockerLoading} onDigilocker={handleDigilocker}
                                        docs={docs} setDoc={setDoc} submitting={submitting} onSubmit={onSubmit}
                                    />
                                )
                            )}
                        </>
                    )}

                </div>

                {/* ── RIGHT SIDEBAR ── */}
                <div className="w-72 hidden xl:flex flex-col gap-4 shrink-0 h-full overflow-y-auto no-scrollbar p-4 md:p-8 md:pl-0">
                    <div className="bg-white rounded-3xl shadow-sm p-5 flex flex-col gap-5">

                        <div>
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Wing Ratio Status</span>
                            <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-1">Network Symmetry</h3>
                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-slate-600">Wing symmetry</span>
                                    <span className="text-sm font-bold text-[#2172E5]">{symPct}% matched</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-[#2172E5] rounded-full transition-all duration-500" style={{ width: `${symPct}%` }} />
                                </div>
                                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                                    Matching {minWing} pairs of your {maxWing} potential {symSide} Wing channels.
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-slate-100 pt-4">
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Team Channels</span>
                            <div className="mt-3 space-y-1">
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer">
                                    <span className="text-sm font-bold text-[#10B1B6]">Left Wing Leaders</span>
                                    <div className="flex -space-x-1.5">
                                        {leftLeaders.map((m, i) => (
                                            <div key={m.id ?? i} className="w-6 h-6 rounded-full bg-[#10B1B6]/15 text-[#10B1B6] text-[9px] font-black grid place-items-center ring-2 ring-white">
                                                {initials(m.full_name)}
                                            </div>
                                        ))}
                                        {leftLeaders.length === 0 && <span className="text-xs text-slate-300">—</span>}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer">
                                    <span className="text-sm font-bold text-[#E45B80]">Right Wing Growth</span>
                                    <div className="flex -space-x-1.5">
                                        {rightLeaders.map((m, i) => (
                                            <div key={m.id ?? i} className="w-6 h-6 rounded-full bg-[#E45B80]/15 text-[#E45B80] text-[9px] font-black grid place-items-center ring-2 ring-white">
                                                {initials(m.full_name)}
                                            </div>
                                        ))}
                                        {rightLeaders.length === 0 && <span className="text-xs text-slate-300">—</span>}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer">
                                    <span className="text-sm font-bold text-[#7059FD]">Direct Referrals</span>
                                    <div className="flex -space-x-1.5">
                                        {[...leftLeaders.slice(0, 1), ...rightLeaders.slice(0, 1)].map((m, i) => (
                                            <div key={i} className="w-6 h-6 rounded-full bg-[#7059FD]/15 text-[#7059FD] text-[9px] font-black grid place-items-center ring-2 ring-white">
                                                {initials(m.full_name)}
                                            </div>
                                        ))}
                                        {(leftLeaders.length + rightLeaders.length === 0) && <span className="text-xs text-slate-300">—</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-slate-100 pt-4">
                            <button
                                onClick={copyInvite}
                                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-[#2172E5] hover:text-blue-700 transition-colors"
                            >
                                {copied
                                    ? <><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Copied!</>
                                    : <><Copy className="w-4 h-4" /> + Copy Partner Invite Link</>
                                }
                            </button>
                        </div>

                    </div>
                </div>

            </div>
        </AppShell>
    );
}

/* ── Verification forms (shared by approved re-verify + non-approved flows) ── */
function VerificationForm({
    selectedMethod, onBack,
    otpSent, setOtpSent, aadhaarNumber, setAadhaarNumber, otp, setOtp,
    otpLoading, verifying, onSendOtp, onVerifyOtp,
    panNumber, setPanNumber, panLoading, onVerifyPan,
    digilockerLoading, onDigilocker,
    docs, setDoc, submitting, onSubmit,
}) {
    return (
        <div className="bg-white rounded-3xl shadow-sm p-8">
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-slate-700 mb-6 transition-colors"
            >
                <ArrowRight className="w-4 h-4 rotate-180" /> Back to methods
            </button>

            {/* Aadhaar OTP */}
            {selectedMethod === "aadhaar_otp" && (
                <div>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Aadhaar Verification</span>
                    <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-0.5 mb-6">Enter Aadhaar Details</h3>
                    {!otpSent ? (
                        <div className="space-y-4 max-w-md">
                            <div>
                                <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block mb-2">Aadhaar Number</label>
                                <input
                                    type="text" value={aadhaarNumber}
                                    onChange={e => setAadhaarNumber(e.target.value.replace(/\D/g, "").slice(0, 12))}
                                    placeholder="Enter 12-digit number"
                                    className="w-full px-4 py-3 bg-slate-50 rounded-2xl text-sm focus:ring-2 focus:ring-[#7059FD] outline-none"
                                />
                            </div>
                            <button onClick={onSendOtp} disabled={otpLoading || aadhaarNumber.length !== 12}
                                className="w-full py-3 bg-[#7059FD] hover:bg-purple-700 disabled:bg-slate-200 text-white font-bold rounded-2xl transition-colors flex items-center justify-center gap-2">
                                {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                                Send OTP
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4 max-w-md">
                            <p className="text-sm text-slate-500">OTP sent to mobile linked with Aadhaar ending in {aadhaarNumber.slice(-4)}</p>
                            <div>
                                <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block mb-2">6-digit OTP</label>
                                <input
                                    type="text" value={otp}
                                    onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    placeholder="000000"
                                    className="w-full px-4 py-3 bg-slate-50 rounded-2xl text-sm text-center text-lg tracking-widest focus:ring-2 focus:ring-[#7059FD] outline-none"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => { setOtpSent(false); setOtp(""); }}
                                    className="flex-1 py-3 border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold rounded-2xl transition-colors">
                                    Back
                                </button>
                                <button onClick={onVerifyOtp} disabled={verifying || otp.length !== 6}
                                    className="flex-1 py-3 bg-[#7059FD] hover:bg-purple-700 disabled:bg-slate-200 text-white font-bold rounded-2xl transition-colors flex items-center justify-center gap-2">
                                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                    Verify OTP
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* PAN */}
            {selectedMethod === "pan" && (
                <div>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">PAN Verification</span>
                    <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-0.5 mb-6">Enter PAN Details</h3>
                    <div className="space-y-4 max-w-md">
                        <div>
                            <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block mb-2">PAN Number</label>
                            <input
                                type="text" value={panNumber}
                                onChange={e => setPanNumber(e.target.value.toUpperCase().slice(0, 10))}
                                placeholder="AAAAA9999A"
                                className="w-full px-4 py-3 bg-slate-50 rounded-2xl text-sm uppercase focus:ring-2 focus:ring-[#E45B80] outline-none"
                            />
                            <p className="text-xs text-slate-400 mt-1">Format: 5 letters, 4 numbers, 1 letter</p>
                        </div>
                        <button onClick={onVerifyPan} disabled={panLoading || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)}
                            className="w-full py-3 bg-[#E45B80] hover:bg-pink-700 disabled:bg-slate-200 text-white font-bold rounded-2xl transition-colors flex items-center justify-center gap-2">
                            {panLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                            Verify PAN
                        </button>
                    </div>
                </div>
            )}

            {/* Document Upload */}
            {selectedMethod === "upload" && (
                <form onSubmit={onSubmit}>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Document Upload</span>
                    <h3 className="text-xl font-extrabold text-[#0C2B5C] mt-0.5 mb-2">Upload KYC Documents</h3>
                    <p className="text-sm text-slate-400 mb-6">Clear, readable images. JPG / PNG / PDF · max 5 MB each.</p>
                    <div className="space-y-4">
                        <DocUpload label="Aadhaar Card — Front" hint="JPG / PNG / PDF · max 5 MB" file={docs.aadhaar_front} onChange={setDoc("aadhaar_front")} />
                        <DocUpload label="Aadhaar Card — Back"  hint="JPG / PNG / PDF · max 5 MB" file={docs.aadhaar_back}  onChange={setDoc("aadhaar_back")}  />
                        <DocUpload label="PAN Card"              hint="JPG / PNG / PDF · max 5 MB" file={docs.pan}           onChange={setDoc("pan")}           />
                        <DocUpload label="Selfie / Photograph"   hint="Clear face photo · max 5 MB" file={docs.selfie}       onChange={setDoc("selfie")}        />
                    </div>
                    <div className="mt-5 p-4 bg-amber-50 border border-amber-100 rounded-2xl text-xs text-amber-800">
                        Documents must be in your name and match registration details. Aadhaar and PAN are required for bonus releases.
                    </div>
                    <button type="submit" disabled={submitting}
                        className="mt-5 w-full py-3 bg-[#F59E0B] hover:bg-amber-600 disabled:opacity-70 text-white font-bold rounded-2xl transition-colors flex items-center justify-center gap-2">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {submitting ? "Uploading…" : "Submit for Verification"}
                    </button>
                </form>
            )}
        </div>
    );
}
